import React, { useState, useEffect } from 'react';
import {
  Box,
  Spinner,
  VStack,
  Text,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';

const MediaLoader = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [responseMessage, setResponseMessage] = useState(null);
  const toast = useToast();

  useEffect(() => {
    // Listen for media loading start event
    const handleStartLoading = (event) => {
      setIsLoading(true);
      setLoadingMessage(event.detail.message || 'Sending media...');
      setResponseMessage(null);
    };

    // Listen for media loading complete event
    const handleLoadingComplete = (event) => {
      // Stop loading and close overlay immediately
      setIsLoading(false);
      setLoadingMessage('');
      setResponseMessage(null);

      // Only show a brief toast; do not keep the success/warning card visible
      if (event.detail.success) {
        toast({
          title: 'Success',
          description: event.detail.message || 'Media sent to conversation successfully!',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Warning',
          description: event.detail.message || 'Vehicle created but failed to send stored media. You can upload it manually later.',
          status: 'warning',
          duration: 2000,
          isClosable: true,
        });
      }
    };

    window.addEventListener('mediaLoadingStart', handleStartLoading);
    window.addEventListener('mediaLoadingComplete', handleLoadingComplete);

    return () => {
      window.removeEventListener('mediaLoadingStart', handleStartLoading);
      window.removeEventListener('mediaLoadingComplete', handleLoadingComplete);
    };
  }, [toast]);

  if (!isLoading && !responseMessage) {
    return null;
  }

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="blackAlpha.600"
      zIndex={9999}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Box
        bg="white"
        borderRadius="lg"
        p={8}
        boxShadow="xl"
        maxW="400px"
        w="90%"
        textAlign="center"
      >
        {isLoading ? (
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text fontSize="lg" fontWeight="medium" color="gray.700">
              {loadingMessage || 'Sending Media'}
            </Text>
            <Text fontSize="sm" color="gray.500">
              Please wait while we upload your media...
            </Text>
          </VStack>
        ) : responseMessage ? (
          <Alert
            status={responseMessage.status}
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="auto"
            borderRadius="md"
          >
            <AlertIcon boxSize="40px" mr={0} />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              {responseMessage.title}
            </AlertTitle>
            <AlertDescription maxWidth="sm">
              {responseMessage.description}
            </AlertDescription>
          </Alert>
        ) : null}
      </Box>
    </Box>
  );
};

export default MediaLoader;

